import { useState } from 'react';
import { useUserState } from '../../hooks/context/useUserState';
import { useResponseActionsContext } from '../../hooks/context/useResponseActionsContext';
import useApiErrorHandling from '../../hooks/useApiErrorHandling';
import { putLayout, PutLayoutResponse } from '../requests/layout/putLayout';
import { apiRequest } from '../utils/apiRequest';
import { detailedApiMethodFetcher } from '../utils/fetchers/detailedApiMethodFetcher';
import { HandlerRequestContext } from '../types/types';
import { DetailedApiMethod, JSONObject, Layout } from '../../types/types';

export const useSaveLayout = () => {
  const {
    state: { token },
  } = useUserState();
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const handleApiError = useApiErrorHandling();
  const { getResponseActionHandler } = useResponseActionsContext();

  const save = async (
    api: string,
    layout: Layout,
    forceHeader?: string
  ): Promise<PutLayoutResponse | undefined> => {
    try {
      setIsSaving(true);
      setError(undefined);
      return await putLayout(api, layout, token, forceHeader);
    } catch (e) {
      if (e instanceof Error) setError(e);
      handleApiError(e, false, (retryForceHeader) =>
        save(api, layout, retryForceHeader)
      );
    } finally {
      setIsSaving(false);
    }
  };

  // apiRequest resolves the {{ }} templates against resolveData, fires the
  // request and runs the method's responseActions on the response. The body
  // goes in AFTER the resolution — user values (captions) must not be
  // mistaken for templates.
  const detailedSave = async (
    apiMethod: DetailedApiMethod,
    resolveData: HandlerRequestContext,
    layout: Layout,
    forceHeader?: string
  ): Promise<PutLayoutResponse | undefined> => {
    try {
      setIsSaving(true);
      setError(undefined);
      return await apiRequest<PutLayoutResponse>(
        apiMethod,
        resolveData,
        (resolvedMethod) =>
          detailedApiMethodFetcher<PutLayoutResponse>(
            {
              ...resolvedMethod,
              httpMethod: resolvedMethod.httpMethod ?? 'PUT',
              content: layout as unknown as JSONObject,
            },
            forceHeader
          ),
        getResponseActionHandler
      );
    } catch (e) {
      if (e instanceof Error) setError(e);
      handleApiError(e, false, (retryForceHeader) =>
        detailedSave(apiMethod, resolveData, layout, retryForceHeader)
      );
    } finally {
      setIsSaving(false);
    }
  };

  return { isSaving, error, save, detailedSave };
};
